import { useMemo } from 'react';
import type {
  AssistantConversationPhase,
  AssistantSceneEvents,
  AssistantSceneModel,
  AssistantWorkspaceKind,
  AIAssistantPrototypePreviewPane,
  AIAssistantPrototypeSurface,
  AIAssistantPrototypeUtilityTab,
  AIAssistantPrototypeWidthMode,
} from '@triedge/ai-assistant-ui';

export type AssistantSceneParityState = {
  panelVisibility?: 'open' | 'closed';
  widthMode?: AIAssistantPrototypeWidthMode;
  surface?: AIAssistantPrototypeSurface;
  utilityTab?: AIAssistantPrototypeUtilityTab;
  utilityPaneOpen?: boolean;
  widthMenuOpen?: boolean;
  modelMenuOpen?: boolean;
  previewPane?: 'inline-summary' | 'workspace' | null;
};

export const useAssistantSceneController = (input: {
  widthMode: AIAssistantPrototypeWidthMode;
  onWidthModeChange?: (mode: AIAssistantPrototypeWidthMode) => void;
  onSurfaceChange?: (surface: AIAssistantPrototypeSurface) => void;
  onUtilityTabChange?: (tab: AIAssistantPrototypeUtilityTab) => void;
  onUtilityPaneOpenChange?: (open: boolean) => void;
  onWidthMenuOpenChange?: (open: boolean) => void;
  parityState?: AssistantSceneParityState | null;
  previewPane?: AIAssistantPrototypePreviewPane | null;
  workspaceKind?: AssistantWorkspaceKind | null;
  conversationPhase?: AssistantConversationPhase;
  onClose?: () => void;
  onResetConversation?: () => void;
  onWelcomeSubmit?: (question: string) => boolean;
}) => {
  const scene = useMemo<AssistantSceneModel>(() => ({
    panelVisibility: input.parityState?.panelVisibility ?? 'open',
    surface: input.parityState?.surface ?? (input.widthMode === 'full' ? 'full-chat' : 'rail-chat'),
    widthMode: input.parityState?.widthMode ?? input.widthMode,
    utilityTab: input.parityState?.utilityTab,
    utilityPaneOpen: input.parityState?.utilityPaneOpen,
    widthMenuOpen: input.parityState?.widthMenuOpen,
    modelMenuOpen: input.parityState?.modelMenuOpen,
    previewPane: input.previewPane,
    workspace: input.previewPane ? {
      kind: input.workspaceKind ?? 'artifact-preview',
      title: input.previewPane.title,
      content: input.previewPane.content,
      backLabel: 'Back',
    } : null,
    conversationPhase: input.conversationPhase ?? 'idle',
    mobileSidebarOpen: false,
  }), [
    input.conversationPhase,
    input.parityState?.panelVisibility,
    input.parityState?.modelMenuOpen,
    input.parityState?.surface,
    input.parityState?.utilityPaneOpen,
    input.parityState?.utilityTab,
    input.parityState?.widthMenuOpen,
    input.parityState?.widthMode,
    input.previewPane,
    input.widthMode,
    input.workspaceKind,
  ]);

  const events = useMemo<AssistantSceneEvents>(() => ({
    onWidthModeChange: input.onWidthModeChange,
    onSurfaceChange: input.onSurfaceChange,
    onUtilityTabChange: input.onUtilityTabChange,
    onUtilityPaneOpenChange: input.onUtilityPaneOpenChange,
    onWidthMenuOpenChange: input.onWidthMenuOpenChange,
    onClose: input.onClose,
    onResetConversation: input.onResetConversation,
    onWelcomeSubmit: input.onWelcomeSubmit,
  }), [
    input.onClose,
    input.onResetConversation,
    input.onSurfaceChange,
    input.onUtilityPaneOpenChange,
    input.onUtilityTabChange,
    input.onWelcomeSubmit,
    input.onWidthMenuOpenChange,
    input.onWidthModeChange,
  ]);

  return { scene, events };
};
